import { type FC } from "react";

import { BasicLink } from "@styled/basic_link";
import { Button } from "@styled/button";

import { NewCaseInline } from "./new_case_inline";
import { type StatusSegment } from "./projects_list";

// Empty, but not a first run. Which one is decided by the ladder in
// `projects_list.tsx` — the test is whether the USER caused the emptiness.
export type NoResultsKind = "filtered" | "bucket" | "archived";

// A zero bucket is usually a success ("every case has records"), so it gets a
// statement rather than an undo.
const BUCKET_COPY: Partial<
  Record<StatusSegment, { head: string; sub?: string }>
> = {
  processing: {
    head: "No cases are processing right now.",
    sub: "They'll appear here while Workflow is organizing their records.",
  },
  needsUpload: {
    head: "Every case has records.",
    sub: "Nothing is waiting on an upload.",
  },
  readyForReview: { head: "Nothing is ready for review yet." },
  archived: { head: "You haven't archived anything yet." },
};

export const ListNoResults: FC<{
  kind: NoResultsKind;
  status: StatusSegment;
  archivedCount: number;
  onClear: () => void;
}> = ({ kind, status, archivedCount, onClear }) => {
  const copy =
    kind === "filtered"
      ? {
          head: "No cases match these filters.",
          sub: "Clear them to see every case again.",
        }
      : kind === "archived"
        ? {
            head: "All your cases are archived.",
            sub: "Nothing is active right now. Start a new case, or reopen one from the archive.",
          }
        : (BUCKET_COPY[status] ?? { head: "Nothing here yet." });

  return (
    <div className="py-6">
      <div className="flex items-start gap-4">
        <div className="min-w-0">
          <h2 className="text-[17px] font-bold text-slate-900">{copy.head}</h2>
          {copy.sub && (
            <p className="mt-0.5 text-[12px] text-slate-500">{copy.sub}</p>
          )}
        </div>

        {kind === "archived" && (
          <div className="ml-auto shrink-0 text-[12px]">
            <BasicLink to="/dashboard/projects?status=archived">
              View archived ({archivedCount})
            </BasicLink>
          </div>
        )}
      </div>

      {kind === "filtered" && (
        <div className="mt-4">
          <Button type="button" color="white" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        </div>
      )}

      {kind === "archived" && <NewCaseInline />}
    </div>
  );
};
